import clsx from 'clsx';
import * as React from 'react';

import { diff } from '@gitbook/doc-core';

import { shortcuts, useKeyboardShortcuts } from '../../../Keyboard';
import type { RightGutterComponentType } from '../../ContentEditor';
import { useComments } from '../useComments';
import { CommentButton } from './CommentButton';

/**
 * Button displayed in the right gutter of the editor, for adding or reading comments on a block.
 */
export const BlockCommentButton = React.memo(function BlockCommentButton(
    props: React.ComponentProps<RightGutterComponentType>
) {
    const { nodeKey } = props;

    // We want to store comments on the original key and not the virtual diff one.
    const diffKey = diff.parseDiffKey(nodeKey);

    if (diffKey && !diffKey.primary) {
        return null;
    }

    return <BlockCommentButtonEnabled {...props} nodeKey={diffKey?.originalKey ?? nodeKey} />;
});

function BlockCommentButtonEnabled(props: React.ComponentProps<RightGutterComponentType>) {
    const { nodeKey, visible, style, shortcutsActive } = props;

    const { hasComments, openNodeComments, nodeState } = useComments({
        nodeKey,
    });

    // Bind the shortcut to open this palette.
    useKeyboardShortcuts(
        shortcutsActive
            ? {
                  [shortcuts.EDITOR_COMMENT]: () => openNodeComments(),
              }
            : {},
        ['editor-selection-collapsed', 'editor-selection-expanded']
    );

    return (
        <div
            className={clsx(
                hasComments && 'page-has-comments',
                'flex min-w-fit items-start justify-start gap-1 transition max-md:hidden',
                visible || hasComments || nodeState.addNewPopoverIsOpen
                    ? 'opacity-100'
                    : 'opacity-0 hover:opacity-100 group-hover/gutter:opacity-100 has-[button[aria-expanded="true"]]:opacity-100'
            )}
            style={style}
            data-hide-on-drag
        >
            <CommentButton nodeKey={nodeKey} iconOnly shortenLabel label="Comment on block" />
        </div>
    );
}
